import { watch, type WatchStopHandle } from 'vue'
import type { Route } from 'vue-router'
import type VueRouter from 'vue-router'

import { type SearchFilters, type SortKey } from '@/typings/twitter_benchmark_campaign_posts'

import { useTwBenchmarkPostsStore } from '../store/useTwBenchmarkPostsStore'
import {
  listStateToQuery,
  type Order,
  type PageSize,
  queryToListState,
  type TwBenchmarkListState,
  validateAndCorrectQuery
} from '../utils/queryParams'

const BASE_PATH = '/twitter_benchmark_analytics'

export interface UseTwBenchmarkQuerySyncOptions {
  router?: VueRouter
  route?: Route
}

/**
 * 競合キャンペーン分析ページの URL クエリ同期（URL クエリを単一情報源とする一方向ループを Composition API 向けに実装し、fetch を watch ハンドラ 1 箇所に集約して二重取得を防ぐ）。
 */
export function useTwBenchmarkQuerySync(options: UseTwBenchmarkQuerySyncOptions = {}) {
  const { router, route } = options
  const store = useTwBenchmarkPostsStore()

  let lastQueryKey: string | null = null
  let stopWatch: WatchStopHandle | null = null

  const getRoute = (): Route | undefined => router?.currentRoute ?? route

  const toQueryKey = (query: Record<string, string>): string =>
    Object.keys(query)
      .sort()
      .map(key => `${key}=${query[key]}`)
      .join('&')

  const runHydrateAndFetch = async (query: Record<string, string | (string | null)[]>): Promise<void> => {
    const currentRoute = getRoute()
    const { correctedQuery, hasErrors } = validateAndCorrectQuery(query)
    if (hasErrors && router !== undefined && currentRoute !== undefined) {
      await router.replace({ path: currentRoute.path, query: correctedQuery }).catch(() => {
        // NavigationDuplicated 等は無視する
      })
      return
    }

    const key = toQueryKey(correctedQuery)
    if (key === lastQueryKey) return
    lastQueryKey = key

    store.hydrateFromState(queryToListState(query))
    await store.fetchPosts()
  }

  const hydrateAndFetch = async (): Promise<void> => {
    const currentRoute = getRoute()
    if (currentRoute === undefined) return
    if (currentRoute.path !== BASE_PATH) return
    await runHydrateAndFetch(currentRoute.query)
  }

  const start = (): Promise<void> => {
    const currentRoute = getRoute()
    if (currentRoute === undefined) return Promise.resolve()
    stopWatch?.()

    const initialRun = runHydrateAndFetch(currentRoute.path === BASE_PATH ? currentRoute.query : {})

    stopWatch = watch(() => getRoute()?.query, hydrateAndFetch, { deep: true })
    return initialRun
  }

  const stop = (): void => {
    stopWatch?.()
    stopWatch = null
  }

  const currentListState = (): TwBenchmarkListState => ({
    filters: store.searchFilters as SearchFilters,
    sortBy: store.sortBy as SortKey,
    order: store.order as Order,
    page: store.currentPage,
    pageSize: store.pageSize as PageSize
  })

  const syncUrlFromStore = (): void => {
    const currentRoute = getRoute()
    if (router === undefined || currentRoute === undefined) return
    const query = listStateToQuery(currentListState())
    if (currentRoute.path !== BASE_PATH) return
    lastQueryKey = toQueryKey(query)
    router.push({ path: currentRoute.path, query }).catch(() => {
      // 同一クエリへの push は NavigationDuplicated で reject されるため無視する
    })
  }

  const loadPage = async (page: number): Promise<void> => {
    const nextState: TwBenchmarkListState = { ...currentListState(), page }
    lastQueryKey = toQueryKey(listStateToQuery(nextState))
    store.hydrateFromState(nextState)
    await store.fetchPosts()
  }

  return {
    BASE_PATH,
    start,
    stop,
    syncUrlFromStore,
    loadPage
  }
}

export type UseTwBenchmarkQuerySync = ReturnType<typeof useTwBenchmarkQuerySync>
